import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {IonicModule, ModalController, NavController} from '@ionic/angular';
import {TranslatePipe} from '@ngx-translate/core';
import {PhotoBoxComponent} from '../../photo-box/photo-box.component';
import {MapPrimaryButtonComponent} from '../map-primary-button/map-primary-button.component';
import {MapStatisticCirclesComponent} from '../map-statistic-circles/map-statistic-circles.component';
import {ActionPointListModule} from "../../action-point-list/action-point-list.module";
import {NgForOf, NgIf, NgSwitch, NgSwitchCase} from "@angular/common";
import {MapMarkerModel} from "../../../models/map/map-marker/map-marker.model";
import {MapToggleBarComponent} from "../map-toggle-bar/map-toggle-bar.component";
import {SidePanelDataService} from "../../../services/z-map/side-panel-data-service";
import {CompanyModel} from "../../../models/company.model";
import {ApiResponse, handleError} from "../../../services/utils";
import {CompanyService} from "../../../services/company/company.service";
import {CommonMyApModule} from "../../common-my-ap/common-my-ap.module";
import {CompanyEmployeeModule} from "../../company-employee/company-employee.module";
import {FollowerListModule} from "../../follower-list/follower-list.module";
import {TeamListModule} from "../../team-list/team-list.module";
import {UserModel} from "../../../models/user.model";
import {AuthService} from "../../../services/auth/auth.service";
import {GlobalService} from "../../../services/global/global.service";
import {MapSecondaryButtonComponent} from "../map-secondary-button/map-secondary-button.component";
import {NavigationService} from "../../../services/navigations/navigation.service";
import {FeedService} from "../../../services/feed/feed.service";
import {PostModel} from "../../../models/post.model";
import {TeamModel} from "../../../models/team.model";
import {TeamService} from "../../../services/team/team.service";
import {TeamMembersModule} from "../../team-members/team-members.module";
import {BehaviorSubject, Subscription} from "rxjs";
import {SelectComponentComponent} from "../../select-component/select-component.component";
import {getDetailPointOfAction} from "../../../services/variables";
import {catchError} from "rxjs/operators";
import {PointOfActionModel} from "../../../models/map/point-of-action.model";
import {HttpClient} from "@angular/common/http";
import {StripUrlPipe} from "../../../pipes/strip-url.pipe";
import {PostCardComponent} from "../../post-card/post-card.component";

@Component({
  selector: 'app-map-sheet-modal',
  templateUrl: './map-sheet-modal.component.html',
  styleUrls: ['./map-sheet-modal.component.scss'],
  imports: [
    IonicModule,
    TranslatePipe,
    PhotoBoxComponent,
    MapPrimaryButtonComponent,
    MapStatisticCirclesComponent,
    ActionPointListModule,
    NgIf,
    MapToggleBarComponent,
    CommonMyApModule,
    CompanyEmployeeModule,
    FollowerListModule,
    NgForOf,
    NgSwitchCase,
    TeamListModule,
    NgSwitch,
    MapSecondaryButtonComponent,
    TeamMembersModule,
    StripUrlPipe,
    PostCardComponent,
  ],
  standalone: true
})
export default class MapSheetModalComponent {
  @Input() set marker(value: MapMarkerModel | undefined) {
    if (!value) {
      this._marker = value;
      return;
    }

    if (value.details && typeof value.details === 'string') {
      try {
        this._marker = {
          ...value,
          details: JSON.parse(value.details as unknown as string),
        };
      } catch (e) {
        console.error('[MapSheetModal] Failed to parse marker.details', e);
        this._marker = value;
      }
    } else {
      this._marker = value;
      this.currentSegment = 'posts';
      if (this._marker.type === 'organization') {
        this.loadCompany(this._marker.id);
      } else if (this._marker.type === 'space') {
        this.loadSpace(this._marker.id);
      } else if (this._marker.type === 'poa') {
        const pointOfActionId: number = this._marker.id;

        let url = `${getDetailPointOfAction}/${pointOfActionId}`;

        this.http.get<ApiResponse>(url, {
          headers: this.authService.jsonheader
        })
          .pipe(
            catchError(handleError)
          )
          .subscribe({
            next: (response) => {
              this.pointOfAction = response.data;
            },
            error: (err) => console.error('API error:', err)
          });
      }
    }
  }

  @Output() closed = new EventEmitter<void>();

  private _marker?: MapMarkerModel;
  public currentSegment: string = 'posts';
  public organization: CompanyModel | null = null;
  public space: TeamModel | null = null;
  public pointOfAction: PointOfActionModel = null;
  public user: UserModel = null;
  public errorMessage: string[] = [];

  // Company loaders
  private companyLoaders: Record<string, () => void> = {
    posts: (): void => this.dataService.loadCompanyPosts(this.organization?.id),
    action_points: (): void => this.dataService.loadCompanyActionPoints(this.organization?.id),
    teams: (): void => this.dataService.loadCompanyTeams(this.organization?.id),
    follower: (): void => this.dataService.loadCompanyFollowers(this.organization?.id),
    employees: (): void => this.dataService.loadCompanyEmployees(this.organization?.id),
  };

  // Space loaders
  private spaceLoaders: Record<string, () => void> = {
    posts: (): void => this.dataService.loadSpacePosts(this.space?.id),
    action_points: (): void => this.dataService.loadSpaceActionPoints(this.space?.id),
    members: (): void => this.dataService.loadSpaceMembers(this.space?.id),
  };

  get marker(): MapMarkerModel | undefined {
    return this._marker;
  }

  get posts(): PostModel[] {
    return this.dataService.posts.items;
  }

  get actionPoints(): PostModel[] {
    return this.dataService.actionPoints.items;
  }

  get teams(): TeamModel[] {
    return this.dataService.teams.items;
  }

  get followers(): UserModel[] {
    return this.dataService.followers.items;
  }

  get employees(): UserModel[] {
    return this.dataService.employees.items;
  }

  get spacePosts(): PostModel[] {
    return this.dataService.spacePosts.items;
  }

  get spaceMembers(): UserModel[] {
    return this.dataService.spaceMembers.items;
  }

  get spaceActionPoints(): PostModel[] {
    return this.dataService.spaceActionPoints.items;
  }

  get loading() {
    return this.dataService.posts.loading || this.dataService.spacePosts.loading;
  }

  constructor(
    private dataService: SidePanelDataService,
    private teamService: TeamService,
    private companyService: CompanyService,
    private authService: AuthService,
    private navController: NavController,
    public globalService: GlobalService,
    private navigationService: NavigationService,
    private feedService: FeedService,
    private modalCtrl: ModalController,
    private http: HttpClient,
  ) {
  }

  ngOnInit(): void {
    this.user = this.authService.user;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['marker'] && this._marker?.type === 'organization') {
      this.currentSegment = 'posts';
      this.loadCompany(this._marker.id);
    }
  }

  public loadCompany(id: number | string): void {
    this.companyService.apiViewCompany(id).subscribe((result: ApiResponse): void => {
      if (result.status) {
        this.organization = result.data as CompanyModel;
        this.dataService.reset();
        this.dataService.loadCompanyPosts(this.organization.id);
      } else {
        this.errorMessage = result.error ?? ['Unknown error'];
      }
    });
  }

  public loadSpace(id: number | string): void {
    this.teamService.apiViewTeam(id).subscribe((result: ApiResponse): void => {
      if (result.status) {
        this.space = result?.data;
        this.organization = null;
        this.dataService.reset();
        this.dataService.loadSpacePosts(this.space.id);

        this.teamService.teamEvent.next(result.data);
        this.globalService.postactionsurveyEvent.next({type: 'team', id: result.data.id});
      } else {
        this.errorMessage = result.error;
      }
    });
  }

  public onSegmentChanged(value: string): void {
    this.currentSegment = value;
    this.dataService.reset();
    if (this.organization) {
      this.companyLoaders[value]?.();
    } else if (this.space) {
      this.spaceLoaders[value]?.();
    }
  }

  public fullLoaded(): boolean {
    if (this.organization) {
      return !this.dataService[this.currentSegment]?.maxDataLoaded;
    } else if (this.space) {
      return !this.dataService[`space${this.capitalize(this.currentSegment)}`]?.maxDataLoaded;
    }
    return false;
  }

  public loadData(event: any): void {
    if (this.organization) {
      this.companyLoaders[this.currentSegment]?.();
    } else if (this.space) {
      this.spaceLoaders[this.currentSegment]?.();
    }
    event.target.complete();
  }

  public async openOtherProfile(id: string | number): Promise<void> {
    if (!this.organization.createdBy.isDeleted) {
      await this.navController.navigateForward(['/app/home/other-profile/' + id]);
      await this.closeModal();
    }
  }

  public async openActionPoint(actionPointId: number): Promise<void> {
    await this.navigationService.openActionPointDetail(actionPointId);
    await this.closeModal();
  }

  public async navigateToCompanyProfile(company: CompanyModel): Promise<void> {
    await this.navigationService.openCompanyProfile(company);
    await this.closeModal();
  }

  public async openCompanyProfileById(id: number, isAdmin: boolean): Promise<void> {
    await this.navigationService.openCompanyProfileById(id, isAdmin);
    await this.closeModal();
  }

  public async clickFollow(company: CompanyModel): Promise<void> {
    this.organization.isFollowed = true;
    try {
      const result: boolean = await this.feedService.followCompany(company, this.user);

      if (!result) {
        this.organization.isFollowed = false;
        await this.isAnonymousAction();
      }
    } catch (error) {
      this.organization.isFollowed = false;
    }
  }

  public async clickUnfollow(company: CompanyModel): Promise<void> {
    this.organization.isFollowed = false;

    try {
      const result: boolean = await this.feedService.unfollowCompany(company);

      if (!result) {
        this.organization.isFollowed = true;
      }
    } catch (error) {
      this.organization.isFollowed = true;
    }
  }

  public async openSpaceProfile(space: TeamModel): Promise<void> {
    await this.navigationService.openTeamProfile(space);
    await this.closeModal();
  }

  public async joinTeamModal(): Promise<void> {
    if (this.authService.isAuthenticated) {
      const mySubject = new BehaviorSubject<any>(null);

      const modal: HTMLIonModalElement = await this.modalCtrl.create({
        component: SelectComponentComponent,
        id: 'team-select-modal',
        backdropDismiss: true,
        componentProps: {
          mySubject,
          teamId: this.space.id,
        },
      });

      await modal.present();

      const sub: Subscription = mySubject.subscribe((value: any): void => {
        if (value !== null) {
          this.teamService.joinOrLeaveTeam(this.space.id, value, this.space)
            .subscribe((result: ApiResponse): void => {
              if (result.status) {
                this.globalService.presentToast(result.message);
              } else {
                this.globalService.presentToast(result.error[0]);
              }
            });
        }
      });

      modal.onDidDismiss().then((): void => {
        sub.unsubscribe();
      });
    } else {
      await this.globalService.openLoginModal();
    }
  }

  public async closeModal() {
    await this.modalCtrl.dismiss(null, 'closed');
  }

  //----------------------- Helper methods -----------------------//
  private async isAnonymousAction(): Promise<void> {
    if (!(this.user && this.authService.isAuthenticated)) {
      await this.globalService.openLoginModal();
    }
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}

